import { useRef, createRef, Fragment, useLayoutEffect } from 'react'
import classNames from 'classnames'
import wpautop from 'wpautop'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { createUseStyles } from 'react-jss'
import SimpleMedia from '@/components/SimpleMedia'
import SimpleSplitText from '@/components/SimpleSplitText'
import useScrollTrigger from '@/hooks/useScrollTrigger'
import { removeParagraph, splitAfterBr } from '@/utils/strings'
import style from './style'

gsap.registerPlugin(ScrollTrigger)

const useStyles = createUseStyles(style)

const ImageText = ({
  align,
  className,
  img,
  text,
  title,
  background,
}) => {
  const { animationReady } = useScrollTrigger()
  const classes = useStyles()
  const trigger = useRef([])
  const $titleWrapper = useRef()
  const $titles = useRef(splitAfterBr(removeParagraph(title)).map(() => createRef()))
  const $imageWrapper = useRef()
  const $image = useRef()
  const $textWrapper = useRef()

  const fadeIn = (ref, duration = 1.2) => {
    gsap.to(ref, { opacity: 1, ease: 'power3.out', duration })
  }

  useLayoutEffect(() => {
    if (animationReady) {
      if ($titles.current.length > 0) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $titleWrapper.current,
          onEnter: () => $titles.current.map((t) => t.current.animateIn()),
        }))
      }
      if ($textWrapper.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $textWrapper.current,
          onEnter: () => fadeIn($textWrapper.current),
        }))
      }
      if (img) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $imageWrapper.current,
          onEnter: () => $image.current.animateIn(),
        }))
      }
    }
    return () => {
      if (trigger.current.length > 0) trigger.current.map((tr) => tr.kill())
    }
  }, [animationReady])

  const renderImage = () => {
    return (
      <SimpleMedia
        ref={$image}
        className={classes.media}
        src={img}
        alt={title}
        prepareAnimation
      />
    )
  }

  const renderTitle = () => {
    const splittedTitle = splitAfterBr(removeParagraph(title))
    return splittedTitle.map((t, i) => (
      <Fragment key={i.toString()}>
        <SimpleSplitText
          ref={$titles.current[i]}
          text={t}
          disableHover
          animation="split"
          appear
        />
      </Fragment>
    ))
  }

  const renderText = () => {
    return <div dangerouslySetInnerHTML={{ __html: wpautop(text) }} />
  }

  return (
    <>
      <div
        className={classNames({
          [classes.root]: true,
          [classes.rootAlignRight]: align === 'right',
          [classes.rootAlignLeft]: align === 'left',
          [classes.rootBackground]: background,
          [className]: className,
          Hero__root: true,
        })}
      >
        <div className={classes.content}>
          <div className={classes.info}>
            <div ref={$titleWrapper} className={`${classes.title} title-hero`}>
              {renderTitle()}
            </div>
            {img ? (
              <div ref={$imageWrapper} className={classes.mediaContainer}>
                {renderImage()}
              </div>
            ) : null}
            {text ? (
              <div
                ref={$textWrapper}
                className={classes.text}
              >
                {renderText()}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

ImageText.defaultProps = {
  align: 'left',
  className: '',
  title: '',
  text: '',
  img: '',
  background: false,
}

export default ImageText
